import React from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    PersonAdd as PersonAddIcon,
    QrCodeScanner as ScannerIcon,
    Warning as WarningIcon,
} from "@mui/icons-material";

const getResultMeta = ({ passenger, traveler, person }) => {
    if (!passenger && !traveler && !person) {
        return {
            title: "Información no disponible",
            color: "error",
            icon: <ErrorIcon color="error" />,
            bg: "#fff5f5",
        };
    }

    if (passenger) {
        const confirmed = passenger.mandetestpas === "CONFIRMADO";
        return {
            title: confirmed ? "Pasajero ya confirmado" : "Pasajero encontrado",
            color: confirmed ? "warning" : "success",
            icon: confirmed ? <WarningIcon color="warning" /> : <CheckCircleIcon color="success" />,
            bg: confirmed ? "#fff8e1" : "#f1f8e9",
        };
    }

    if (traveler) {
        return {
            title: traveler.mandetid ? "Viajero ya asignado" : "Viajero de la orden",
            color: traveler.mandetid ? "error" : "info",
            icon: traveler.mandetid ? <ErrorIcon color="error" /> : <PersonAddIcon color="info" />,
            bg: traveler.mandetid ? "#ffebee" : "#e3f2fd",
        };
    }

    return {
        title: "Persona encontrada",
        color: "info",
        icon: <PersonAddIcon color="info" />,
        bg: "#e3f2fd",
    };
};

const PassengerScanner = ({
    dni,
    setDni,
    result,
    setResult,
    onSearch,
    onConfirmPassenger,
    onConfirmTraveler,
    onRegisterPerson,
}) => {
    const { passenger, traveler, person } = result || {};
    const meta = getResultMeta({ passenger, traveler, person });

    const close = () => setResult({ open: false, passenger: null, traveler: null, person: null });

    return (
        <>
            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    borderRadius: 2.5,
                    backgroundColor: "#f8fafc",
                    borderLeft: "5px solid",
                    borderLeftColor: "primary.main",
                }}
            >
                <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <ScannerIcon color="primary" />
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                                Control de pasajeros por DNI
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Escanee o ingrese manualmente el DNI del pasajero.
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                        <TextField
                            label="DNI"
                            value={dni}
                            onChange={(e) => setDni(e.target.value.replace(/\D/g, "").slice(0, 8))}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") onSearch();
                            }}
                            fullWidth
                            size="small"
                            inputProps={{ maxLength: 8 }}
                            helperText={dni && dni.length !== 8 ? "Debe tener 8 dígitos." : " "}
                        />
                        <Button
                            variant="contained"
                            onClick={onSearch}
                            disabled={dni.length !== 8}
                            sx={{ minWidth: 140, height: 40 }}
                        >
                            Buscar
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            <Dialog open={Boolean(result?.open)} onClose={close} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {meta.icon}
                        <Typography fontWeight="bold">{meta.title}</Typography>
                    </Stack>
                </DialogTitle>

                <DialogContent sx={{ backgroundColor: meta.bg }}>
                    {!passenger && !traveler && !person && (
                        <DialogContentText>
                            No se encontró información para el DNI <strong>{dni}</strong>.
                        </DialogContentText>
                    )}

                    {passenger && (
                        <Stack spacing={1}>
                            <Typography>
                                <strong>{passenger.pernom} {passenger.perape}</strong>
                            </Typography>
                            <Typography variant="body2">
                                DNI: <strong>{dni}</strong>
                            </Typography>
                            <Chip
                                label={passenger.mandetestpas}
                                color={passenger.mandetestpas === "CONFIRMADO" ? "success" : "info"}
                                sx={{ width: "fit-content" }}
                            />
                            {passenger.mandetestpas !== "CONFIRMADO" && (
                                <Alert severity="success" variant="outlined">
                                    El pasajero está en el manifiesto. Puede confirmar su asistencia.
                                </Alert>
                            )}
                        </Stack>
                    )}

                    {traveler && (
                        <Stack spacing={1}>
                            <Typography>
                                <strong>{traveler.pernom} {traveler.perape}</strong>
                            </Typography>
                            <Typography variant="body2">
                                DNI: <strong>{dni}</strong>
                            </Typography>
                            {traveler.mandetid ? (
                                <Alert severity="error" variant="outlined">
                                    Este viajero ya está asignado a otro manifiesto.
                                </Alert>
                            ) : (
                                <Alert severity="info" variant="outlined">
                                    El viajero pertenece a la orden, pero aún no está en este manifiesto.
                                </Alert>
                            )}
                        </Stack>
                    )}

                    {person && (
                        <Stack spacing={1}>
                            <Typography>
                                <strong>{person.pernom} {person.perape}</strong>
                            </Typography>
                            <Typography variant="body2">
                                DNI: <strong>{dni}</strong>
                            </Typography>
                            <Alert severity="warning" variant="outlined">
                                La persona existe, pero no está registrada en este manifiesto.
                            </Alert>
                        </Stack>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={close}>Cerrar</Button>

                    {passenger && passenger.mandetestpas !== "CONFIRMADO" && (
                        <Button variant="contained" onClick={() => onConfirmPassenger(passenger)}>
                            Confirmar asistencia
                        </Button>
                    )}

                    {traveler && !traveler.mandetid && (
                        <Button variant="contained" onClick={() => onConfirmTraveler(traveler)}>
                            Asignar y confirmar
                        </Button>
                    )}

                    {person && (
                        <Button variant="contained" onClick={() => onRegisterPerson(person)}>
                            Registrar invitado
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PassengerScanner;